/**
 * useQueueSync — global WebSocket → TanStack Query cache invalidation.
 *
 * Mount once at the AppShell level. On every `queue_updated` event the gateway
 * emits, this hook invalidates:
 *   - ['queues']          — the flat list (QueuesView)
 *   - ['queues', queueId] — all sub-keys for that queue (status, items, runs)
 *
 * This replaces all per-view polling intervals and per-view WS listeners for
 * queue data. REST endpoints are still used for the actual fetches; WS is only
 * the invalidation signal.
 */
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { getGatewaySocket } from '../lib/gatewaySocket';
import type { QueueDashboardEntry, QueueStatus } from '../types/queue';
import type { QueueUpdatedEvent } from '../types/socket';

export function useQueueSync(token: string | null | undefined): void {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!token) return;
    const socket = getGatewaySocket(token);

    const handler = (data: QueueUpdatedEvent) => {
      // Apply worker snapshot directly to cache so the active-workers widget
      // updates instantly, without waiting for the REST refetch to complete.
      if (data.event === 'worker_changed' && data.activeWorkers !== undefined) {
        const workers = data.activeWorkers;

        // Update queue list (QueuesView)
        queryClient.setQueryData<QueueDashboardEntry[]>(
          ['queues'],
          (prev) =>
            prev?.map((q) =>
              q.id === data.queueId
                ? {
                    ...q,
                    activeWorkers: workers.length,
                    activeWorkerWiIds: workers.map((w) => w.workItemId),
                    state: (!q.enabled
                      ? 'disabled'
                      : workers.length > 0
                        ? 'working'
                        : 'idle') satisfies QueueDashboardEntry['state'],
                  }
                : q,
            ) ?? prev,
        );

        // Update queue status cache (QueueDetailView) so claimedIds → isRunning
        // updates immediately without waiting for the REST refetch.
        queryClient.setQueryData<QueueStatus>(['queues', data.queueId, 'status'], (prev) =>
          prev
            ? {
                ...prev,
                state: (!prev.definition.enabled
                  ? 'disabled'
                  : workers.length > 0
                    ? 'working'
                    : 'idle') satisfies QueueStatus['state'],
                activeWorkers: workers.map((w) => ({
                  id: w.workerId,
                  queueId: data.queueId,
                  wiId: w.workItemId,
                  agentInstance: w.agentIdentifier,
                  startedAt: w.startedAt,
                  heartbeatAt: w.startedAt,
                  status: (w.status === 'active' ? 'running' : 'stalled') as 'running' | 'stalled',
                })),
              }
            : prev,
        );
      }

      // Bust the queue list (exact match — don't cascade to all sub-keys here)
      void queryClient.invalidateQueries({ queryKey: ['queues'], exact: true });
      // Bust all sub-keys for the affected queue: status, items, runs
      void queryClient.invalidateQueries({ queryKey: ['queues', data.queueId] });
      // Bust agent caches so AgentDetailView reflects the new worker state immediately
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
      void queryClient.invalidateQueries({ queryKey: ['agent-workers'] });
    };

    socket.on('queue_updated', handler);
    return () => {
      socket.off('queue_updated', handler);
    };
  }, [token, queryClient]);
}
